import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { useHighlightCells } from '~/hooks/useHighlightCells'
import { useTamRatio } from '~/hooks/useTamRatio'
import {
  compact,
  getDiffKey,
  pathAssume,
  QuestionOp,
  type RowInfo,
} from '~/utils'

import {
  useAtomValueContext,
  useSetAtomContext,
} from '../providers/AtomsProvider'
import {
  buildSequentialTableInfo,
  getAnswerOptionRowId,
  type SequentialColumnKey,
} from '../utils'

import { useCustomerAttributesArrayData } from './useCustomerAttributesArrayData'
import { useGetSortIcon } from './useGetSortIcon'
import { useGroupAndAllNameMap } from './useGroupAndAllNameMap'
import { useMapGroup } from './useMapGroup'
import { useMapGroupsDiff } from './useMapGroupsDiff'
import { useNormalAnswersArrayData } from './useNormalAnswersArrayData'
import { useSortFunc } from './useSortFunc'
import { useTotalData } from './useTotalData'

type SequentialTableInfoStyles = {
  answerText: string
  questionText: string
}

export const useSequentialTableInfo = (
  styles: SequentialTableInfoStyles,
): RowInfo<SequentialColumnKey>[] => {
  const { t } = useTranslation()
  const groupsDiffs = useAtomValueContext('groupsDiffs')
  const dataDisplayType = useAtomValueContext('dataDisplayType')
  const tamRatio = useTamRatio()
  const totalData = useTotalData()
  const mapGroup = useMapGroup()
  const mapGroupsDiff = useMapGroupsDiff()
  const groupAndAllNameMap = useGroupAndAllNameMap()
  const getSortIcon = useGetSortIcon('sequentialSortValue')
  const setNameEditingGroup = useSetAtomContext('nameEditingGroup')
  const { addHighlightProps } = useHighlightCells('sequential')
  const hiddenRowIds = useAtomValueContext('hiddenRowIds')
  const answersArrayData = useNormalAnswersArrayData()
  const customerAttributesArrayData = useCustomerAttributesArrayData()
  const sortFunc = useSortFunc('sequentialSortValue')

  const visibleAnswersArrayData = React.useMemo(
    () =>
      answersArrayData
        .map((answersData) =>
          R.differenceWith(
            answersData,
            hiddenRowIds,
            ({ answerOption, question }, hiddenRowId) =>
              getAnswerOptionRowId(question, answerOption) === hiddenRowId,
          ),
        )
        .filter((answersData) => 0 < answersData.length),
    [answersArrayData, hiddenRowIds],
  )

  const visibleCustomerAttributesArrayData = React.useMemo(
    () =>
      customerAttributesArrayData
        .map((customerAttributesData) =>
          R.differenceWith(
            customerAttributesData,
            hiddenRowIds,
            ({ customerAttribute }, hiddenRowId) =>
              customerAttribute.id === hiddenRowId,
          ),
        )
        .filter((customerAttributesData) => 0 < customerAttributesData.length),
    [customerAttributesArrayData, hiddenRowIds],
  )

  const diffMinMaxMap = React.useMemo(
    () =>
      mapGroupsDiff((groupsDiff) => {
        const ratioArray = R.pipe(
          R.concat(answersArrayData, customerAttributesArrayData),
          R.flatMap((rowsData) =>
            rowsData.map((rowData) => rowData.ratios[getDiffKey(groupsDiff)]),
          ),
          compact,
        )

        return { max: Math.max(...ratioArray), min: Math.min(...ratioArray) }
      }),
    [answersArrayData, customerAttributesArrayData, mapGroupsDiff],
  )

  return React.useMemo(
    () =>
      buildSequentialTableInfo({
        addHighlightProps,
        customerAttributesRowGetters: {
          getAnswerText: pathAssume('customerAttribute.name'),
          getQuestionText: pathAssume('customerAttributeGroup.name'),
          getRowId: pathAssume('customerAttribute.id'),
        },
        dataDisplayType,
        diffMinMaxMap,
        getSortIcon,
        groupAndAllNameMap,
        groupsDiffs,
        mapGroup,
        mapGroupsDiff,
        normalAnswersRowGetters: {
          getAnswerText: pathAssume('answerOption.text'),
          getQuestionText: (answerData) =>
            QuestionOp.toText(answerData.question),
          getRowId: (answerData) =>
            getAnswerOptionRowId(answerData.question, answerData.answerOption),
        },
        setNameEditingGroup,
        sortFunc,
        styles: {
          answerText: styles.answerText,
          questionText: styles.questionText,
        },
        t,
        tamRatio,
        totalData: { counts: totalData.counts, ratios: totalData.ratios },
        visibleAnswersArrayData,
        visibleCustomerAttributesArrayData,
      }),
    [
      addHighlightProps,
      dataDisplayType,
      diffMinMaxMap,
      getSortIcon,
      groupAndAllNameMap,
      groupsDiffs,
      mapGroup,
      mapGroupsDiff,
      setNameEditingGroup,
      sortFunc,
      styles.answerText,
      styles.questionText,
      t,
      tamRatio,
      totalData.counts,
      totalData.ratios,
      visibleAnswersArrayData,
      visibleCustomerAttributesArrayData,
    ],
  )
}
